import { motion } from 'framer-motion';
import { cases } from '../../../mocks/cases';

interface CaseDetailModalProps {
  caseId: string;
  onClose: () => void;
}

const CaseDetailModal = ({ caseId, onClose }: CaseDetailModalProps) => {
  const caseData = cases.find(c => c.id === caseId);

  if (!caseData) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部 */}
        <div className="sticky top-0 bg-gradient-to-r from-[#1A3A5C] to-[#2A4A6C] px-8 py-6 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <i className={`${caseData.icon} text-[#FFB800] text-3xl`}></i>
            <div>
              <h2 className="text-2xl font-bold text-white">{caseData.typeLabel}</h2>
              <p className="text-sm text-white/80 mt-1">{caseData.background}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors flex-shrink-0"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* 内容 */}
        <div className="p-8">
          {/* 客户画像 */}
          <section className="mb-8">
            <h3 className="text-lg font-bold text-neutral-900 mb-4 flex items-center gap-2">
              <i className="ri-user-line text-[#FFB800]"></i>
              客户画像
            </h3>
            <div className="bg-neutral-50 rounded-xl p-6 space-y-3">
              <div className="flex items-start gap-3">
                <span className="text-sm font-bold text-neutral-600 w-24 flex-shrink-0">行业类型</span>
                <span className="text-sm text-neutral-900">{caseData.details.profile.industry}</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-sm font-bold text-neutral-600 w-24 flex-shrink-0">客户类型</span>
                <span className="text-sm text-neutral-900">{caseData.details.profile.customerType}</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-sm font-bold text-neutral-600 w-24 flex-shrink-0">原有模式</span>
                <span className="text-sm text-neutral-900">{caseData.details.profile.originalMode}</span>
              </div>
            </div>
          </section>

          {/* 原有痛点 */}
          <section className="mb-8">
            <h3 className="text-lg font-bold text-neutral-900 mb-4 flex items-center gap-2">
              <i className="ri-error-warning-line text-red-500"></i>
              原有痛点
            </h3>
            <div className="space-y-3">
              {caseData.details.painPoints.map((point, index) => (
                <div key={index} className="flex items-start gap-3 bg-red-50 rounded-lg p-4">
                  <i className="ri-close-circle-line text-red-500 text-lg mt-0.5 flex-shrink-0"></i>
                  <div>
                    <p className="font-bold text-sm text-neutral-900 mb-1">{point.title}</p>
                    <p className="text-sm text-neutral-700">{point.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* 1039 解决路径 */}
          <section className="mb-8">
            <h3 className="text-lg font-bold text-neutral-900 mb-4 flex items-center gap-2">
              <i className="ri-lightbulb-flash-line text-[#FFB800]"></i>
              1039 解决路径
            </h3>
            <div className="space-y-3">
              {caseData.details.solutions.map((solution, index) => (
                <div key={index} className="flex items-start gap-3 bg-[#FFB800]/10 rounded-lg p-4">
                  <i className="ri-checkbox-circle-line text-[#FFB800] text-lg mt-0.5 flex-shrink-0"></i>
                  <div>
                    <p className="font-bold text-sm text-neutral-900 mb-1">{solution.policy}</p>
                    <p className="text-sm text-neutral-700 mb-2">{solution.implementation}</p>
                    {solution.systemModule && (
                      <span className="inline-block px-3 py-1 bg-[#1A3A5C] text-white text-xs rounded-full whitespace-nowrap">
                        系统模块：{solution.systemModule}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* 实施效果 */}
          <section className="mb-8">
            <h3 className="text-lg font-bold text-neutral-900 mb-4 flex items-center gap-2">
              <i className="ri-line-chart-line text-green-600"></i>
              实施效果
            </h3>
            <div className="bg-green-50 rounded-xl p-6 space-y-3">
              {caseData.details.effects.map((effect, index) => (
                <div key={index} className="flex items-start gap-3">
                  <i className="ri-check-double-line text-green-600 text-lg mt-0.5 flex-shrink-0"></i>
                  <p className="text-sm text-neutral-900">{effect}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 适用人群总结 */}
          <section className="mb-8">
            <div className="bg-gradient-to-br from-[#1A3A5C] to-[#2A4A6C] rounded-xl p-6 text-white">
              <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                <i className="ri-group-line text-[#FFB800]"></i>
                适用人群总结
              </h3>
              <p className="text-base leading-relaxed">
                {caseData.details.targetAudience}
              </p>
            </div>
          </section>

          {/* 合规声明 */}
          <div className="bg-neutral-100 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-2">
              <i className="ri-information-line text-neutral-600 text-lg mt-0.5 flex-shrink-0"></i>
              <p className="text-xs text-neutral-600 leading-relaxed">
                <strong>合规声明：</strong>以上案例基于真实业务场景整理，数据已做区间化处理。具体业务适用性需根据实际情况判断，最终以海关及相关监管部门要求为准。本案例不构成任何承诺或保证。
              </p>
            </div>
          </div>

          {/* 行动按钮 */}
          <div className="flex gap-4">
            <button
              onClick={() => {
                onClose();
                window.REACT_APP_NAVIGATE('/test');
              }}
              className="flex-1 py-4 bg-[#FFB800] text-white rounded-xl font-bold hover:bg-[#E5A600] transition-colors whitespace-nowrap"
            >
              <i className="ri-test-tube-line mr-2"></i>
              测试我是否适合 1039
            </button>
            <button
              onClick={onClose}
              className="px-8 py-4 border-2 border-[#1A3A5C] text-[#1A3A5C] rounded-xl font-bold hover:bg-[#1A3A5C] hover:text-white transition-colors whitespace-nowrap"
            >
              关闭
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CaseDetailModal;